/**
 * CompareHeights (shared)
 *
 * Equalizes the height of grouped elements so cards/columns line up even when
 * their content differs (e.g. a feature card with no body copy). Wrap a set of
 * elements that each carry a matching `data-compare-row` value; the tallest in
 * each group sets the height for the rest. Recalculates on resize, DOM changes,
 * and when the element becomes visible.
 */
class CompareHeights extends HTMLElement {
  constructor() {
    super();
    this.resizeObserver = null;
    this.mutationObserver = null;
    this.intersectionObserver = null;

    // Flag to prevent duplicate calculations in the same frame
    this._isAlignmentScheduled = false;
  }

  connectedCallback() {
    // Force block display to ensure accurate measurements
    this.style.display = 'block';
    this.init();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.mutationObserver) this.mutationObserver.disconnect();
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
  }

  init() {
    // 1. Watch for screen size changes
    this.resizeObserver = new ResizeObserver(() => {
      this.scheduleAlign();
    });
    this.resizeObserver.observe(this);

    // 2. Watch for DOM changes (swapping products)
    this.mutationObserver = new MutationObserver(() => {
      this.scheduleAlign();
    });

    this.mutationObserver.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // 3. Watch for Visibility Changes (Tab Switching)
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.scheduleAlign();
        }
      });
    });
    this.intersectionObserver.observe(this);

    // Initial run
    if (document.readyState === 'complete') {
      this.scheduleAlign();
    } else {
      window.addEventListener('load', () => this.scheduleAlign(), { once: true });
    }
  }

  // Coalesce updates into a single frame
  scheduleAlign() {
    if (this._isAlignmentScheduled) return;

    this._isAlignmentScheduled = true;

    requestAnimationFrame(() => {
      this.alignHeights();
      this._isAlignmentScheduled = false;
    });
  }

  alignHeights() {
    // Safety check: if hidden, stop.
    if (this.offsetParent === null) return;

    const allRows = this.querySelectorAll('[data-compare-row]');

    // Reset heights to auto to read natural height
    // (We do this in a batch to minimize reflows)
    allRows.forEach((el) => (el.style.height = 'auto'));

    const rowGroups = {};

    // Group elements
    allRows.forEach((el) => {
      const rowId = el.getAttribute('data-compare-row');
      if (!rowGroups[rowId]) {
        rowGroups[rowId] = [];
      }
      rowGroups[rowId].push(el);
    });

    // Calculate and Apply
    Object.keys(rowGroups).forEach((key) => {
      const elements = rowGroups[key];
      let maxHeight = 0;

      // READ phase (causes reflow)
      elements.forEach((el) => {
        if (el.offsetHeight > maxHeight) maxHeight = el.offsetHeight;
      });

      // WRITE phase (invalidates layout)
      if (maxHeight > 0) {
        elements.forEach((el) => (el.style.height = `${maxHeight}px`));
      }
    });

    // Let consumers react once heights are settled (e.g. fade a section in).
    this.dispatchEvent(new CustomEvent('compare-heights:aligned', { bubbles: true }));
  }
}

if (!customElements.get('compare-heights')) {
  customElements.define('compare-heights', CompareHeights);
}
